import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Stack,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useProject } from '../contexts/ProjectContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { 
  getArticleDetail, 
  updateArticle, 
  generateArticlePlan, 
  confirmArticlePlan, 
  writeParagraph, 
  updateArticleStatus, 
  deleteParagraph, 
  createParagraph, 
  getProjectSettings, 
  getArticleResources,
  saveArticlePlan,
  writeParagraphStream
} from '../services/api';
import EditParagraphDialog from './dialogs/EditParagraphDialog';
import useLLMModels from '../hooks/useLLMModels';
import { useSnackbar } from 'notistack';
import { useCoins } from '../contexts/CoinContext';
import { useAuth } from '../contexts/AuthContext';
import ArticleParagraphs from './article/ArticleParagraphs';
import ArticleResources from './article/ArticleResources';
import ArticlePlan from './article/ArticlePlan';
import ImageGeneratorDialog from './ImageGeneratorDialog';
import FullscreenImageDialog from './dialogs/FullscreenImageDialog';
import AddParagraphDialog from './dialogs/AddParagraphDialog';
import ArticleDetailsCard from './article/ArticleDetailsCard';
import ArticleTopBar from './article/ArticleTopBar';
import EditArticleDialog from './dialogs/EditArticleDialog';

const ArticleDetail = () => {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    title: '',
    topic: '',
    tags: [],
    deployed_path: '',
    publish_date: ''
  });
  const [planLoading, setPlanLoading] = useState(false);
  const [editParagraphDialog, setEditParagraphDialog] = useState(false);
  const [editingParagraph, setEditingParagraph] = useState(null);
  const [imageGeneratorOpen, setImageGeneratorOpen] = useState(false);
  const [selectedParagraph, setSelectedParagraph] = useState(null);
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const [addParagraphDialogOpen, setAddParagraphDialogOpen] = useState(false);
  const [insertPosition, setInsertPosition] = useState(1);
  const { selectedProject } = useProject();
  const { models, loading: modelsLoading } = useLLMModels();
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedWriteModel, setSelectedWriteModel] = useState('');
  const [settings, setSettings] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const { spendCoins, updateCoins } = useCoins();
  const [resources, setResources] = useState([]);
  const [initialPromptContent, setInitialPromptContent] = useState(null);
  const { showSubscriptionDialog } = useSubscription();
  const { user } = useAuth();
  const [currentParagraphIndex, setCurrentParagraphIndex] = useState(-1);
  const firstLoadRef = useRef(true);
  
  useEffect(() => {
    loadArticle();
    if (selectedProject?.id) {
      loadProjectSettings();
    }
    loadResources();
  }, [articleId, selectedProject]);

  // Add new effect for handling write_first parameter
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const shouldWriteFirst = searchParams.get('write_first') === 'true';

    const writeFirstParagraph = async () => {
      if (article && shouldWriteFirst && firstLoadRef.current) {
        firstLoadRef.current = false;
        
        // Find the first empty paragraph
        const firstEmptyParagraph = article.paragraphs
          .sort((a, b) => a.order - b.order)
          .find(p => !p.content || !p.content.trim());

        if (firstEmptyParagraph) {
          try {
            setPlanLoading(true);
            setCurrentParagraphIndex(firstEmptyParagraph.id);
            const { stream, close } = writeParagraphStream(articleId, firstEmptyParagraph.id, selectedWriteModel);
            
            let currentContent = '';
            
            stream.addEventListener('message', (event) => {
              try {
                const data = JSON.parse(event.data);
                
                if (data.error) {
                  console.error('Stream error:', data.error);
                  enqueueSnackbar(data.error, { variant: 'error' });
                  close();
                  return;
                }

                if (!data.is_complete) {
                  currentContent += data.content;
                  // Update the paragraph's content in real-time
                  const updatedParagraphs = [...article.paragraphs];
                  const paragraphToUpdate = updatedParagraphs.find(p => p.id === firstEmptyParagraph.id);
                  if (paragraphToUpdate) {
                    paragraphToUpdate.content = currentContent;
                    setArticle(prev => ({
                      ...prev,
                      paragraphs: updatedParagraphs
                    }));
                  }
                } else {
                  // Final chunk with token counts and credits
                  if (data.credits_used) {
                    spendCoins(data.credits_used);
                    enqueueSnackbar(`-${data.credits_used.toFixed(2)} coins used`, { variant: 'info' });
                  }
                  close();
                  setCurrentParagraphIndex(-1);
                  // Show success message
                  enqueueSnackbar('First paragraph has been written successfully, please review!', { 
                    variant: 'success',
                    autoHideDuration: 4000
                  });
                  // Remove the write_first parameter from URL
                  navigate(`/articles/${articleId}`, { replace: true });
                }
              } catch (error) {
                console.error('Error parsing stream data:', error);
                enqueueSnackbar('Error processing response', { variant: 'error' });
                close();
              }
            });

            stream.addEventListener('error', (error) => {
              console.error('Stream error:', error);
              enqueueSnackbar('Error streaming content', { variant: 'error' });
              close();
            });
          } catch (error) {
            console.error('Error writing first paragraph:', error);
            enqueueSnackbar('Error writing first paragraph', { variant: 'error' });
          } finally {
            setPlanLoading(false);
            setCurrentParagraphIndex(-1);
          }
        }
      }
    };

    writeFirstParagraph();
  }, [article, location.search]);

  const loadArticle = async () => {
    try {
      const data = await getArticleDetail(articleId);
      setArticle(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadProjectSettings = async () => {
    try {
      const settings = await getProjectSettings(selectedProject.id);
      setSettings(settings);
      setSelectedModel(settings.default_paragraph_planner_model || '');
      setSelectedWriteModel(settings.default_paragraph_writer_model || '');
    } catch (error) {
      console.error('Error loading project settings:', error);
    }
  };

  const handleEditClick = () => {
    setEditForm({
      title: article.title,
      topic: article.topic,
      tags: article.tags || [],
      deployed_path: article.deployed_path || '',
      publish_date: article.publish_date ? new Date(article.publish_date).toISOString().slice(0,16) : ''
    });
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
  };

  const handleEditSave = async () => {
    try {
      // Clean up the form data before sending
      const formData = {
        ...editForm,
        // Convert empty string to null for publish_date
        publish_date: editForm.publish_date || null
      };
      
      await updateArticle(article.id, formData);
      await loadArticle();
      setEditDialogOpen(false);
    } catch (err) {
      setError('Error updating article: ' + err.message);
    }
  };

  const handleProjectResourcesChange = async (checked) => {
    try {
      const updatedArticle = await updateArticle(articleId, {
        use_project_resources: checked
      });
      // Update the entire article state with the response
      setArticle(updatedArticle);
      enqueueSnackbar('Project resources setting updated', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar('Error updating project resources setting', { variant: 'error' });
    }
  };

  const handleGeneratePlan = async () => {
    try {
      setPlanLoading(true);
      const response = await generateArticlePlan(articleId, selectedModel);
      // Refresh article to get the new plan
      await loadArticle();
      spendCoins(response.coins_used);
      enqueueSnackbar(`-${response.coins_used.toFixed(2)} coins used`, { variant: 'info' });
    } catch (err) {
      // Don't show error for 402 (subscription required)
      if (err.response?.status === 402) {
        // Pass a callback to handle alternative model selection
        showSubscriptionDialog(selectedModel, async (alternativeModel) => {
          setSelectedModel(alternativeModel);
          try {
            setPlanLoading(true);
            const response = await generateArticlePlan(articleId, alternativeModel);
            await loadArticle();
            spendCoins(response.coins_used);
            enqueueSnackbar(`-${response.coins_used.toFixed(2)} coins used`, { variant: 'info' });
          } catch (retryErr) {
            setError('Error generating plan: ' + retryErr.message);
          } finally {
            setPlanLoading(false);
          }
        });
        return;
      }
      setError('Error generating plan: ' + err.message);
    } finally {
      setPlanLoading(false);
    }
  };

  const handleConfirmPlan = async () => {
    try {
      setPlanLoading(true);
      const response = await confirmArticlePlan(articleId);
      // Refresh article to get the new paragraphs
      await loadArticle();
    } catch (err) {
      setError('Error confirming plan: ' + err.message);
    } finally {
      setPlanLoading(false);
    }
  };

  const handleEditParagraph = (paragraph) => {
    console.log("Editing paragraph:", paragraph);
    setEditingParagraph(paragraph);
    setEditParagraphDialog(true);
  };

  const handleSaveParagraph = async () => {
    try {
      setPlanLoading(true);
      await loadArticle();
      setEditParagraphDialog(false);
      setEditingParagraph(null);
    } catch (err) {
      setError(`Error updating paragraph: ${err.message}`);
    } finally {
      setPlanLoading(false);
    }
  };


  const handleImageClick = (imageUrl, alt) => {
    setFullscreenImage({
      imageUrl,
      alt
    });
  };

  const handleStatusChange = async (event) => {
    try {
      const newStatus = event.target.value;
      const updatedArticle = await updateArticleStatus(articleId, newStatus);
      setArticle(updatedArticle);
    } catch (err) {
      setError('Error updating status: ' + err.message);
    }
  };

  const handleDeleteParagraph = async (paragraph) => {
    
    if (!window.confirm(`Are you sure you want to delete the paragraph "${paragraph.heading}"?`)) {
      return;
    }

    try {
      setPlanLoading(true);
      await deleteParagraph(articleId, paragraph.id);
      await loadArticle();
      enqueueSnackbar('Paragraph deleted successfully', { variant: 'success' });
    } catch (err) {
      setError(`Error deleting paragraph: ${err.message}`);
      enqueueSnackbar('Error deleting paragraph', { variant: 'error' });
    } finally {
      setPlanLoading(false);
    }
  };

  const handleAddParagraph = async (paragraphData) => {
    try {
      setPlanLoading(true);
      const response = await createParagraph(articleId, paragraphData);
      await loadArticle();
      setAddParagraphDialogOpen(false);
    } catch (err) {
      setError(`Error adding paragraph: ${err.message}`);
    } finally {
      setPlanLoading(false);
    }
  };

  const handleWriteAllParagraphs = async () => {
    try {
      setPlanLoading(true);
      
      // Sort paragraphs by order and filter empty ones
      const sortedParagraphs = [...article.paragraphs].sort((a, b) => a.order - b.order);
      const emptyParagraphs = sortedParagraphs.filter(p => !p.content || !p.content.trim());
      
      if (emptyParagraphs.length === 0) {
        enqueueSnackbar('No empty paragraphs to write', { variant: 'info' });
        return;
      }

      let totalCoinsUsed = 0;

      // Process each paragraph sequentially
      for (let i = 0; i < emptyParagraphs.length; i++) {
        const paragraph = emptyParagraphs[i];
        setCurrentParagraphIndex(paragraph.id);

        try {
          // Create a promise that resolves when streaming is complete
          await new Promise((resolve, reject) => {
            let currentContent = '';
            const { stream, close } = writeParagraphStream(articleId, paragraph.id, selectedWriteModel);

            stream.addEventListener('message', (event) => {
              try {
                const data = JSON.parse(event.data);
                
                if (data.error) {
                  console.error('Stream error:', data.error);
                  enqueueSnackbar(data.error, { variant: 'error' });
                  close();
                  reject(new Error(data.error));
                  return;
                }

                if (!data.is_complete) {
                  currentContent += data.content;
                  // Update the paragraph's content in real-time
                  const updatedParagraphs = [...article.paragraphs];
                  const paragraphToUpdate = updatedParagraphs.find(p => p.id === paragraph.id);
                  if (paragraphToUpdate) {
                    paragraphToUpdate.content = currentContent;
                    setArticle(prev => ({
                      ...prev,
                      paragraphs: updatedParagraphs
                    }));
                  }
                } else {
                  // Final chunk with token counts and credits
                  if (data.credits_used) {
                    totalCoinsUsed += data.credits_used;
                    spendCoins(data.credits_used);
                    enqueueSnackbar(`-${data.credits_used.toFixed(2)} coins used`, { variant: 'info' });
                  }
                  close();
                  resolve();
                }
              } catch (error) {
                console.error('Error parsing stream data:', error);
                enqueueSnackbar('Error processing response', { variant: 'error' });
                close();
                reject(error);
              }
            });

            stream.addEventListener('error', (error) => {
              console.error('Stream error:', error);
              enqueueSnackbar('Error streaming content', { variant: 'error' });
              close();
              reject(error);
            });
          });

          // Add a small delay between paragraphs
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Refresh article data after each paragraph
          await loadArticle();
          
          // Show progress
          enqueueSnackbar(`Completed ${i + 1} of ${emptyParagraphs.length} paragraphs`, { 
            variant: 'info',
            autoHideDuration: 2000
          });

        } catch (error) {
          console.error(`Error writing paragraph ${paragraph.id}:`, error);
          enqueueSnackbar(`Error writing paragraph: ${error.message}`, { variant: 'error' });
          // Ask user if they want to continue
          if (!window.confirm('Error writing paragraph. Continue with remaining paragraphs?')) {
            break;
          }
        }
      }

      setCurrentParagraphIndex(-1);
      // Update coins first
      updateCoins();

      // Check if all paragraphs now have content
      const allParagraphsHaveContent = article.paragraphs.every(p => p.content && p.content.trim());
      if (allParagraphsHaveContent) {
        // Track article completion event
        window.gtag('event', 'article_completed', {
          'event_category': 'conversion',
          'event_label': 'article_completed',
          'value': 100,
          'currency': 'EUR',
          'article_type': article.type,
          'article_subtype': article.subtype || 'general'
        });
      }

      // Then show the final message
      enqueueSnackbar(`Finished writing all paragraphs. Total coins used: ${totalCoinsUsed.toFixed(2)}`, { 
        variant: 'success',
        autoHideDuration: 5000 // Keep the message longer
      });

    } catch (error) {
      console.error('Error in write all paragraphs:', error);
      enqueueSnackbar('Error writing all paragraphs', { variant: 'error' });
    } finally {
      setPlanLoading(false);
    }
  };

  const handleWriteParagraph = async (paragraph) => {
    try {
      setPlanLoading(true);
      const response = await writeParagraph(
        articleId, 
        paragraph.id, 
        selectedWriteModel
      );
      await loadArticle();

      // Check if all paragraphs now have content after writing this one
      const allParagraphsHaveContent = article.paragraphs.every(p => p.content && p.content.trim());
      if (allParagraphsHaveContent) {
        // Track article completion event
        window.gtag('event', 'article_completed', {
          'event_category': 'conversion',
          'event_label': 'article_completed',
          'value': 100,
          'currency': 'EUR',
          'article_type': article.type,
          'article_subtype': article.subtype || 'general'
        });
      }

      spendCoins(response.coins_used);
      enqueueSnackbar(`-${response.coins_used.toFixed(2)} coins used`, { variant: 'info' });
    } catch (err) {
      if (err.response?.status === 402) {
        // Pass a callback to handle alternative model selection
        showSubscriptionDialog(selectedWriteModel, async (alternativeModel) => {
          setSelectedWriteModel(alternativeModel);
          try {
            setPlanLoading(true);
            const response = await writeParagraph(articleId, paragraph.id, alternativeModel);
            await loadArticle();

            // Check if all paragraphs now have content after writing this one
            const allParagraphsHaveContent = article.paragraphs.every(p => p.content && p.content.trim());
            if (allParagraphsHaveContent) {
              // Track article completion event
              window.gtag('event', 'article_completed', {
                'event_category': 'conversion',
                'event_label': 'article_completed',
                'value': 100, // Value in cents (1 EUR)
                'currency': 'EUR',
                'article_type': article.type,
                'article_subtype': article.subtype || 'general'
              });
            }

            spendCoins(response.coins_used);
            enqueueSnackbar(`-${response.coins_used.toFixed(2)} coins used`, { variant: 'info' });
          } catch (retryErr) {
            enqueueSnackbar('Error writing paragraph: ' + retryErr.message, { variant: 'error' });
          } finally {
            setPlanLoading(false);
          }
        });
        return;
      }
      enqueueSnackbar('Error writing paragraph: ' + err.message, { variant: 'error' });
    } finally {
      setPlanLoading(false);
    }
  };

  const loadResources = async () => {
    try {
      const data = await getArticleResources(articleId);
      setResources(data || []);
    } catch (err) {
      console.error('Error loading resources:', err);
      setResources([]);
    }
  };

  const handleSavePlan = async (newPlan) => {
    try {
      setPlanLoading(true);
      await saveArticlePlan(articleId, newPlan);
      // Refresh article to get the updated plan
      await loadArticle();
    } catch (err) {
      setError('Error saving plan: ' + err.message);
    } finally {
      setPlanLoading(false);
    }
  };

  const handleGenerateImage = (paragraph) => {
    setSelectedParagraph(paragraph);
    setImageGeneratorOpen(true);
  };

  const handleAddParagraphRequest = (request) => {
    if (request.mode === 'dialog') {
      setInsertPosition(request.order);
      setAddParagraphDialogOpen(true);
    }
  };

  if (loading) {
    return <Typography>Loading article...</Typography>;
  }

  if (error) {
    return <Typography color="error">Error: {error}</Typography>;
  }

  if (!article) {
    return <Typography>Article not found</Typography>;
  }

  return (
    <Stack spacing={{ xs: 1, sm: 2 }}>
      <ArticleTopBar
        article={article}
        user={user}
        onEditClick={handleEditClick}
        onImageGeneratorOpen={() => {
          setSelectedParagraph(null);
          setImageGeneratorOpen(true);
        }}
        onTitleChange={async (newTitle) => {
          await updateArticle(article.id, { title: newTitle });
          await loadArticle();
        }}
      />

      {/* Article Details */}
      <Box>
        <ArticleDetailsCard
          article={article}
          onImageClick={handleImageClick}
          onStatusChange={handleStatusChange}
          resources={resources}
          onResourcesChange={loadResources}
          onProjectResourcesChange={handleProjectResourcesChange}
          onPublishDateChange={async (e) => {
            try {
              let publishDate = null;
              if (e.target.value) {
                const localDate = new Date(e.target.value);
                const utcDate = new Date(
                  localDate.getTime() - localDate.getTimezoneOffset() * 60000
                );
                publishDate = utcDate.toISOString();
              }
              
              await updateArticle(article.id, {
                publish_date: publishDate
              });
              await loadArticle();
            } catch (error) {
              console.error('Error updating publish date:', error);
              enqueueSnackbar('Failed to update publish date', { variant: 'error' });
            }
          }}
        />
      </Box>

      {/* Main Content: Plan and Paragraphs */}
      <Box>
        <ArticlePlan
          article={article}
          selectedModel={selectedModel}
          setSelectedModel={setSelectedModel}
          models={models}
          settings={settings}
          planLoading={planLoading}
          modelsLoading={modelsLoading}
          onGeneratePlan={handleGeneratePlan}
          onSavePlan={handleSavePlan}
          onConfirmPlan={handleConfirmPlan}
        />

        <ArticleParagraphs
          article={article}
          selectedModel={selectedWriteModel}
          setSelectedModel={setSelectedWriteModel}
          models={models}
          settings={settings}
          planLoading={planLoading}
          onWriteAllParagraphs={handleWriteAllParagraphs}
          onAddParagraph={handleAddParagraphRequest}
          onWriteParagraph={handleWriteParagraph}
          onEditParagraph={handleEditParagraph}
          onGenerateImage={handleGenerateImage}
          onDeleteParagraph={handleDeleteParagraph}
          onImageClick={handleImageClick}
          currentParagraphIndex={currentParagraphIndex}
          onRewriteComplete={loadArticle}
        />
      </Box>

      {/* Edit Dialog */}
      <EditArticleDialog
        open={editDialogOpen}
        onClose={handleEditClose}
        editForm={editForm}
        setEditForm={setEditForm}
        onSave={handleEditSave}
      />

      <ImageGeneratorDialog
        open={imageGeneratorOpen}
        onClose={() => {
          setImageGeneratorOpen(false);
          setSelectedParagraph(null);
          setInitialPromptContent(null);
        }}
        articleId={articleId}
        paragraphId={selectedParagraph?.id}
        paragraphContent={
          selectedParagraph 
            ? (initialPromptContent || selectedParagraph.content)
            : `Title: ${article.title}\n\n${article.paragraphs.map(p => p.title || '').join('\n\n')}`
        }
        paragraphHeading={selectedParagraph?.heading || article.title}
        onImageGenerated={() => {
          loadArticle();
          setImageGeneratorOpen(false);
          setInitialPromptContent(null);
        }}
        projectId={selectedProject?.id}
      />

      <FullscreenImageDialog
        open={!!fullscreenImage}
        onClose={() => setFullscreenImage(null)}
        imageUrl={fullscreenImage?.imageUrl}
        alt={fullscreenImage?.alt}
      />

      <EditParagraphDialog
        open={editParagraphDialog}
        onClose={() => {
          setEditParagraphDialog(false);
          setEditingParagraph(null);
        }}
        paragraph={editingParagraph}
        articleId={articleId}
        onSave={handleSaveParagraph}
        loading={planLoading}
      />

      <AddParagraphDialog
        open={addParagraphDialogOpen}
        onClose={() => setAddParagraphDialogOpen(false)}
        onAdd={handleAddParagraph}
        position={insertPosition}
      />
    </Stack>
  );
};

export default ArticleDetail;
